/**
 * LeadOpsCard — reusable rectangular ops summary card for a lead.
 * Mirrors LeadsTableView fields; safe to use in grids, period groups, or drawers.
 */

import React from "react";
import { Tag, Typography, Space, Tooltip } from "antd";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import FollowUpMention from "../../followups/components/FollowUpMention.jsx";
import OriginBadge from "./OriginBadge.jsx";
import OperationalStatusBadge from "./OperationalStatusBadge.jsx";
import LeadRiskChips from "./LeadRiskChips.jsx";
import {
  formatLeadQuoteValue,
  getLeadEngine,
  getLeadProviderStatus,
  getLeadQuoteValue,
  getLeadTruckRecommendation,
  getLeadTruckLoadLabel,
} from "../services/leadOperationalStatus.js";
import { formatLeadCreatedDisplay } from "../services/leadCreatedPeriod.js";
import {
  formatTruckLabelWithLoad,
  getLeadBillableVolumeM3,
  getLeadCorridorBadge,
  getLeadDistanceKm,
  getLeadPriorityLabel,
} from "../services/leadJobCockpit.js";

const { Text } = Typography;

const PRIORITY_COLOR = {
  High: "#e65100",
  Medium: "#f9a825",
  Low: "#757575",
};

const ENGINE_TAG_COLOR = {
  shared_load: "blue",
  local: "cyan",
  intra_province: "purple",
  special: "magenta",
  unknown: "default",
};

const formatPhone = (phone) => {
  if (!phone) return "N/A";
  return phone.replace("+27", "0");
};

const CardShell = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 10,
  height: "100%",
  minHeight: 168,
  padding: 16,
  borderRadius: 10,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  cursor: "pointer",
  transition: "box-shadow 0.15s ease, border-color 0.15s ease",
  "&:hover": {
    borderColor: theme.palette.primary.light,
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
  },
}));

const CardHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 8,
});

const TitleBlock = styled(Box)({
  minWidth: 0,
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const LeadName = styled(Box)({
  fontWeight: 600,
  fontSize: 14,
  lineHeight: 1.35,
  color: "rgba(0,0,0,0.88)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const MetaLine = styled(Box)({
  fontSize: 11,
  color: "rgba(0,0,0,0.45)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const RouteBlock = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const RouteLine = styled(Box, {
  shouldForwardProp: (prop) => prop !== "clickable",
})(({ clickable }) => ({
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.35,
  color: clickable ? "#1890ff" : "rgba(0,0,0,0.88)",
  cursor: clickable ? "pointer" : "default",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const BadgeRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
  alignItems: "center",
});

const FooterRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 8,
  marginTop: "auto",
  paddingTop: 4,
});

const Metric = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
});

/**
 * @param {object} props
 * @param {object} props.lead
 * @param {(lead: object) => void} [props.onClick]
 * @param {(lead: object, e: object) => void} [props.onRouteClick]
 * @param {(lead: object) => void} [props.onFollowUpNavigate]
 * @param {React.CSSProperties} [props.style]
 * @param {string} [props.className]
 */
const LeadOpsCard = ({
  lead,
  onClick,
  onRouteClick,
  onFollowUpNavigate,
  style,
  className,
}) => {
  if (!lead) return null;

  const name = lead.name || "Untitled lead";
  const from = lead.moving_from || lead.from;
  const to = lead.moving_to || lead.to;
  const km = getLeadDistanceKm(lead);
  const routeLabel = from && to ? `${from} → ${to}` : "Route pending";
  const engine = getLeadEngine(lead);
  const corridor = getLeadCorridorBadge(lead);
  const size = getLeadTruckRecommendation(lead);
  const load = getLeadTruckLoadLabel(lead);
  const truck = formatTruckLabelWithLoad(size, load);
  const volumeM3 = getLeadBillableVolumeM3(lead);
  const volumeLabel =
    volumeM3 != null
      ? `${Number(volumeM3).toFixed(volumeM3 >= 10 ? 1 : 2)} m³`
      : null;
  const provider = getLeadProviderStatus(lead);
  const priority = getLeadPriorityLabel(lead);
  const created = formatLeadCreatedDisplay(lead);
  const value = formatLeadQuoteValue(getLeadQuoteValue(lead));

  return (
    <CardShell
      className={className}
      style={style}
      onClick={() => onClick?.(lead)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(lead);
        }
      }}
    >
      <CardHeader>
        <TitleBlock>
          <Tooltip title={name}>
            <LeadName>{name}</LeadName>
          </Tooltip>
          {lead.referenceNumber && (
            <MetaLine>{lead.referenceNumber}</MetaLine>
          )}
          <MetaLine>{formatPhone(lead.phone)}</MetaLine>
          <Box mt={0.25}>
            <OriginBadge lead={lead} size="small" />
          </Box>
        </TitleBlock>
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
          <OperationalStatusBadge lead={lead} />
          <Box display="flex" alignItems="center" gap={0.5}>
            <WhatshotIcon
              sx={{ fontSize: 14, color: PRIORITY_COLOR[priority] }}
            />
            <Text
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: PRIORITY_COLOR[priority],
                whiteSpace: "nowrap",
              }}
            >
              {priority}
            </Text>
          </Box>
        </Box>
      </CardHeader>

      <RouteBlock>
        <RouteLine
          clickable={Boolean(onRouteClick && from && to)}
          title={routeLabel}
          onClick={(e) => {
            if (!onRouteClick || !from || !to) return;
            e.stopPropagation();
            onRouteClick(lead, e);
          }}
        >
          {routeLabel}
        </RouteLine>
      </RouteBlock>

      <BadgeRow>
        {km != null && (
          <Tag color="default" style={{ margin: 0 }}>
            {`${Math.round(km)} km`}
          </Tag>
        )}
        <Tag color={ENGINE_TAG_COLOR[engine.key] || "default"} style={{ margin: 0 }}>
          {engine.label}
        </Tag>
        {corridor && (
          <Tag color="geekblue" style={{ margin: 0 }}>
            {corridor}
          </Tag>
        )}
        {truck !== "—" && <Tag style={{ margin: 0 }}>{truck}</Tag>}
        {volumeLabel && (
          <Tag color="default" style={{ margin: 0 }}>
            {volumeLabel}
          </Tag>
        )}
        <Tag style={{ margin: 0 }}>{provider.label}</Tag>
      </BadgeRow>

      <FooterRow>
        <Metric>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Value
          </Text>
          <Text strong style={{ fontSize: 14, whiteSpace: "nowrap" }}>
            {value}
          </Text>
        </Metric>
        <Metric>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Created
          </Text>
          <Tooltip title={created.secondary || undefined}>
            <Text style={{ fontSize: 12, whiteSpace: "nowrap" }}>
              {created.primary}
            </Text>
          </Tooltip>
        </Metric>
        <Space size={8} align="center" onClick={(e) => e.stopPropagation()}>
          <LeadRiskChips lead={lead} max={1} />
          <FollowUpMention
            summary={lead?.followUpSummary}
            onClick={
              onFollowUpNavigate
                ? (e) => {
                    e?.stopPropagation?.();
                    onFollowUpNavigate(lead);
                  }
                : undefined
            }
          />
        </Space>
      </FooterRow>
    </CardShell>
  );
};

export default LeadOpsCard;
